import { useEffect, useMemo, useState } from 'react';
import { User } from '../Dashboard';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import DashboardHeader from '../../../components/DashboardComponents/DashboardHeader/DashboardHeader';
import './DashboardListing.scss';
import {
  changePropertyStatus,
  changeSellPropertyStatus,
  deletePropertiesById,
  deleteSellPropertiesById,
  fetchPropertiesByUser,
  resetListingState,
} from '../../../redux/slices/ListingSlice';
import { getPaginationNumberText } from '../../../utils/common';
import Loader from '../../../components/CommonComponents/Loader/Loader';
import { ToastType, showToastMessage } from '../../../utils/ToastOptionCommon';
import ConfirmationPopupHOC from '../../../utils/ConfirmationPopup/hoc/ConfirmationPopupHoc';
import ConfirmationPopup from '../../../utils/ConfirmationPopup/ConfirmationPopup';
import Pagination from '../../../components/CommonComponents/Pagination/Pagination';
import ListingsRowItem from '../../../components/DashboardComponents/ListingSectionComponents/ListingsSection/ListingsRowItem';
import { AppDispatch } from '../../../redux/store';
import useDebounce from '../../../utils/useDebounce';
import {
  dealType,
  dealTypeEnum,
  propertyType,
} from '../../../constants/Property';

interface IProps {
  showConfirmationPopup: boolean;
  confirmationToggleHandler: () => void;
}

function DashboardListing(props: IProps) {
  const [user, setUser] = useState<User | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    propertyListing,
    isPropertyLoading,
    isPropertyDeleted,
    deleteFavPropertyLoading,
  } = useSelector((state: any) => state.listing);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState('');

  // Filter States
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [dealFilter, setDealFilter] = useState<string>('ALL');
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState<string>(() => {
    const initial = location.state?.initialStatusFilter;

    if (initial === false || initial === 'false' || initial === 'inactive') {
      return 'false';
    }
    if (initial === true || initial === 'true' || initial === 'active') {
      return 'true';
    }
    return 'ALL';
  });

  // Computed Filtered List
  const filteredProperties = useMemo(() => {
    if (!propertyListing?.properties) return [];

    return propertyListing.properties.filter((item: any) => {
      // Normalize values for safe comparison
      const itemStatus = item.status?.toString().toUpperCase() || '';
      const itemType = item.p_type?.toString().toUpperCase() || '';
      const itemDeal = item.dealType?.toString().toUpperCase() || '';
      const itemActive = item.is_active?.toString() || '';

      const matchesStatus =
        statusFilter === 'ALL' || itemStatus === statusFilter.toUpperCase();
      const matchesType =
        typeFilter === 'ALL' || itemType === typeFilter.toUpperCase();
      const matchesDeal =
        dealFilter === 'ALL' || itemDeal === dealFilter.toUpperCase();
      const matchesActive =
        activeFilter === 'ALL' || itemActive === activeFilter;

      return matchesStatus && matchesType && matchesDeal && matchesActive;
    });
  }, [
    propertyListing?.properties,
    statusFilter,
    typeFilter,
    dealFilter,
    activeFilter,
  ]);

  const resetFilters = () => {
    setStatusFilter('ALL');
    setTypeFilter('ALL');
    setDealFilter('ALL');
    setSearchText('');
  };

  const changePage = (event: any) => {
    const pageNumber = event.selected + 1;
    setCurrentPage(pageNumber);
  };

  useDebounce(
    () => {
      if (user !== null) {
        callGetPropertiesAPI();
      }
    },
    [searchText],
    800
  );

  const onSearchTextChanged = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (
      propertyListing.totalPages != undefined &&
      propertyListing.pageSizes != undefined
    ) {
      setTotalPages(propertyListing.totalPages);
      setPageLimit(propertyListing.pageSizes);
    }
  }, [propertyListing.totalPages, propertyListing.pageSizes]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser !== null) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user !== null) {
      callGetPropertiesAPI();
    }
  }, [
    user,
    currentPage,
    pageLimit,
    statusFilter,
    typeFilter,
    dealFilter,
    activeFilter,
  ]);

  const callGetPropertiesAPI = () => {
    dispatch(
      fetchPropertiesByUser({
        userId: user?._id,
        pageNumber: currentPage,
        pageLimit,
        text: searchText,
        // Pass the new filters to backend
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        isActive:
          activeFilter === 'true'
            ? true
            : activeFilter === 'false'
              ? false
              : undefined,
        propertyType: typeFilter !== 'ALL' ? typeFilter : undefined,
        dealType: dealFilter !== 'ALL' ? dealFilter : undefined,
      })
    );
  };

  const handleEdit = (id: string, dealType: string) => {
    navigate(`/dashboard/edit-listing/${id}`, { state: { dealType } });
  };

  const handleDelete = (item: any) => {
    props.confirmationToggleHandler();
    setSelectedItem(() => item);
  };

  const deleteHandler = () => {
    dispatch(
      selectedItem.dealType === dealTypeEnum.Sell
        ? deleteSellPropertiesById(selectedItem._id)
        : deletePropertiesById(selectedItem._id)
    );
    props.confirmationToggleHandler();
  };

  const handleActiveChange = (val: string) => {
    setActiveFilter(val);
    setCurrentPage(1);
  };

  const handleStatusChange = (val: string) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  const handleDealChange = (val: string) => {
    setDealFilter(val);
    setCurrentPage(1);
  };

  const changeStatusProperty = async (
    id: string,
    isActive: boolean,
    status: string,
    dealType: string
  ) => {
    let body = {
      isActive: isActive,
      propertyId: id,
      status: status,
    };

    const resultAction = await dispatch(
      dealType === dealTypeEnum.Sell
        ? changeSellPropertyStatus({ body })
        : changePropertyStatus({ body })
    );

    const actionType =
      dealType === dealTypeEnum.Sell
        ? changeSellPropertyStatus
        : changePropertyStatus;

    if (actionType.fulfilled.match(resultAction)) {
      callGetPropertiesAPI();
    } else if (actionType.rejected.match(resultAction)) {
      const err = resultAction.payload as any;
      if (err?.status === 403) {
        showToastMessage(err?.data?.error, ToastType.Info);
      } else if (err?.status === 400) {
        showToastMessage('No Property found.', ToastType.Error);
      } else {
        showToastMessage('Something went wrong.', ToastType.Error);
      }
    }
  };

  useEffect(() => {
    if (isPropertyDeleted) {
      showToastMessage('Successfully Deleted Property', ToastType.Success);
      callGetPropertiesAPI();
    }
    dispatch(resetListingState({}));
  }, [isPropertyDeleted]);

  const gotoPropertyDetail = (id: string, dealType: string) => {
    navigate(`/property-detail/${dealType || 'rent'}/${id}`, {
      state: { dealType },
    });
  };

  return (
    <>
      {props.showConfirmationPopup && (
        <ConfirmationPopup
          onModalClose={props.confirmationToggleHandler}
          onSubmit={deleteHandler}
          confirmationText={
            'Are you sure, you want to delete the selected property.'
          }
          saveButtonText={'Yes'}
          cancelButtonText={'No'}
          modalTitle={'Delete Property'}
          isProgress={deleteFavPropertyLoading}
        />
      )}

      <div className="main-content">
        {user && (
          <div className="">
            <DashboardHeader />

            <div className="below-header">
              <div className="row align-items-center mb-3">
                <div className="col-sm-7">
                  <h1>My Properties</h1>
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                      <li className="breadcrumb-item">
                        <a href="/dashboard">Dashboard</a>
                      </li>
                      <li
                        className="breadcrumb-item active"
                        aria-current="page"
                      >
                        My Properties
                      </li>
                    </ol>
                  </nav>
                </div>

                {/* Search Bar */}
                <div className="col-sm-5 text-sm-end">
                  <div className="property-filter">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        placeholder="Search with property name"
                        className="form-control w-100"
                        value={searchText}
                        onChange={(e) => onSearchTextChanged(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* --- DROPDOWN FILTERS CONTROL BAR --- */}
              <div className="card shadow-sm px-3 py-2 mb-4 bg-white border border-1 rounded-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  {/* Filter Options Group */}
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    {/* Status Filter */}
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0 text-muted fw-semibold small text-nowrap">
                        Status:
                      </label>
                      <select
                        className="form-select form-select-sm rounded-3 shadow-none border-secondary-subtle"
                        style={{ width: '130px' }}
                        value={statusFilter}
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        <option value="ALL">All Statuses</option>
                        <option value="Available">Available</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Rented">Rented</option>
                      </select>
                    </div>

                    {/* Active Filter */}

                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0 text-muted fw-semibold small text-nowrap">
                        Active:
                      </label>
                      <select
                        className="form-select form-select-sm rounded-3 shadow-none border-secondary-subtle"
                        style={{ width: '130px' }}
                        value={activeFilter}
                        onChange={(e) => handleActiveChange(e.target.value)}
                      >
                        <option value="ALL">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>

                    {/* Property Type Filter */}
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0 text-muted fw-semibold small text-nowrap">
                        Type:
                      </label>
                      <select
                        className="form-select form-select-sm rounded-3 shadow-none border-secondary-subtle"
                        style={{ width: '140px' }}
                        value={typeFilter}
                        onChange={(e) => {
                          setTypeFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="ALL">All Types</option>
                        {propertyType.map((item) => (
                          <option key={item.id} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Deal Type Filter */}
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0 text-muted fw-semibold small text-nowrap">
                        Deal:
                      </label>
                      <select
                        className="form-select form-select-sm rounded-3 shadow-none border-secondary-subtle"
                        style={{ width: '120px' }}
                        value={dealFilter}
                        onChange={(e) => handleDealChange(e.target.value)}
                      >
                        <option value="ALL">All Types</option>
                        {dealType.map((item) => (
                          <option key={item.id} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Reset Action */}
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-pill px-2.5 py-0.5 fw-medium"
                      style={{ fontSize: '0.75rem', lineHeight: '1.2' }}
                      onClick={resetFilters}
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="property-listing listing-table mt-0">
              {isPropertyLoading ? (
                <Loader />
              ) : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Deal Type</th>
                        <th>Date</th>
                        <th>Property Type</th>
                        <th>Is Featured</th>
                        <th>Price</th>
                        <th>Amenities</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProperties && filteredProperties.length > 0 ? (
                        filteredProperties.map((item: any, index: number) => (
                          <ListingsRowItem
                            key={item._id || index}
                            item={item}
                            gotoPropertyDetail={gotoPropertyDetail}
                            handleDelete={handleDelete}
                            handleEdit={handleEdit}
                            changeStatusProperty={changeStatusProperty}
                          />
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={9}
                            className="text-center py-4 text-muted"
                          >
                            No properties match the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {totalPages !== 0 && (
                <Pagination
                  changePage={changePage}
                  totalPages={totalPages}
                  forcePage={currentPage - 1}
                  text={getPaginationNumberText(
                    currentPage,
                    pageLimit,
                    propertyListing?.totalPropertyCount
                  )}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ConfirmationPopupHOC(DashboardListing);
